import React from "react";
import { UpdateCartData } from "@cart-client/interfaces/Checkout";
import { ApiResponse } from "@cart-client/interfaces/Api";
import { MROReviewedCartApiResponse, ReviewedCartApiResponse } from "@swiggy-private/cart-types";
import { FlowType } from "@swiggy-private/checkout-core";
import { SyncApplyCouponCartParams } from "@cart-client/interfaces/Coupon";
import { CouponListingCouponRemovalPayload, MROUpdateCartData } from "@cart-client/interfaces/MROFoodCart";

export interface UpdateCartContextValue {
    updateCartInProgress: boolean;
    updateCartData?: UpdateCartData | MROUpdateCartData | null;
    updateCartError?: { httpStatusCode?: number };
    doUpdateCart: (
        data: NonNullable<UpdateCartData | MROUpdateCartData>,
    ) => Promise<ApiResponse<ReviewedCartApiResponse | MROReviewedCartApiResponse> | null | undefined>;
    reloadCart: () => void;
    performApplyCoupon?: (restaurantId?: string, couponCode?: string) => Promise<void>;
    performRemoveCoupon?: (restaurantId?: string, couponCode?: string) => Promise<void>;
    syncApplyCouponCart: (params: SyncApplyCouponCartParams) => void;
    toggleCouponModal: VoidFunction;
    togglingDelightPopup: VoidFunction;
    showCouponModal: boolean;
    showDelightPopup: boolean;
    showFreebiePopup?: boolean;
    setShowFreebiePopup?: React.Dispatch<React.SetStateAction<boolean>>;
    handleCouponListingPageCouponRemove: (payload: CouponListingCouponRemovalPayload) => void;
    showM2Chalfcard?: boolean;
    setShowM2Chalfcard?: React.Dispatch<React.SetStateAction<boolean>>;
    showM2ChalfcardOnClick?: {
        status: boolean;
        flowType: FlowType; // for analytics and subscriptionNudgeInitiator header
    };
    setShowM2ChalfcardOnClick: React.Dispatch<React.SetStateAction<{ status: boolean; flowType: FlowType }>>;
    /**
     * Optional restaurantId is used by MRO to resolve discounting toast from the
     * child cart that just had apply-coupon. BAU food cart ignores this arg.
     */
    showPendingDiscountingComms: (restaurantId?: string | number) => void;
    /**
     * MRO-only: restaurantId for which apply-coupon was last requested.
     * Used to read coupon_success_card / coupon_error_card from that child cart.
     * Undefined in BAU food cart.
     */
    lastAppliedCouponRestaurantId?: string | number;
}

export const UpdateCartContext = React.createContext<UpdateCartContextValue>({
    updateCartInProgress: false,
    doUpdateCart: async () => null,
    reloadCart: () => void 0,
    showCouponModal: false,
    showDelightPopup: false,
    toggleCouponModal: () => void 0,
    performRemoveCoupon: () => Promise.resolve(void 0),
    performApplyCoupon: () => Promise.resolve(void 0),
    syncApplyCouponCart: () => void 0,
    togglingDelightPopup: () => void 0,
    handleCouponListingPageCouponRemove: () => void 0,
    showM2Chalfcard: false,
    setShowM2Chalfcard: () => void 0,
    showM2ChalfcardOnClick: {
        status: false,
        flowType: FlowType.M2C,
    },
    setShowM2ChalfcardOnClick: () => void 0,
    showPendingDiscountingComms: () => void 0,
    lastAppliedCouponRestaurantId: undefined,
    showFreebiePopup: false,
    setShowFreebiePopup: () => void 0,
});

if (__DEV__) {
    UpdateCartContext.displayName = "UpdateCartContext";
}
